import type { MouseEventHandler } from 'react'

export type CmsCtaIntent = 'link' | 'start_onboarding_wizard' | 'start_onboarding_menu'
export type CmsCtaVariant = 'primary' | 'secondary'

// Injected by the consuming app. When a CTA has trigger_promocode set, clicking
// it fires this callback (e.g. apply promo code + redirect). It receives the
// CTA's url so the app can route after dispatching.
export type OnTriggerPromoCode = (href: string) => void

export type RawCmsCta = {
  text: string
  variant?: CmsCtaVariant
  url?: string
  trigger_promocode?: boolean
  // B20-1020 - Part 2
  // Legacy fields (intent and href) — removed in the CTA cleanup task once content is migrated to
  // url + trigger_promocode.
  intent?: CmsCtaIntent
  href?: string
}

// B20-1020 - Part 2
// Legacy: maps an intent to an injected href/onClick. Removed in the CTA
// cleanup task alongside the `intent` field.
export type CmsCtaIntentHandlers = Partial<
  Record<
    Exclude<CmsCtaIntent, 'link'>,
    {
      href: string
      onClick?: MouseEventHandler<HTMLElement>
    }
  >
>

export type CtaLinkData = {
  text: string
  href: string
  variant?: CmsCtaVariant
  onClick?: MouseEventHandler<HTMLElement>
}

// Drops the query string (and anything after it) from a url, leaving the path.
function stripQueryString(url: string): string {
  return url.split('?')[0]
}

export function mapCmsCta(
  cta?: RawCmsCta,
  intentHandlers: CmsCtaIntentHandlers = {},
  onTriggerPromocode?: OnTriggerPromoCode,
): CtaLinkData | undefined {
  if (!cta?.text) {
    return undefined
  }

  const variant = cta.variant ?? 'primary'

  // New model: an explicit url drives navigation; trigger_promocode opts the
  // click into the promo dispatch. Takes precedence over the legacy intent.
  if (cta.url) {
    const { url } = cta

    return {
      text: cta.text,
      variant,
      // The anchor keeps the full url (including any query string) so plain
      // navigation / right-click still lands on the filtered destination.
      href: url,
      onClick:
        cta.trigger_promocode && onTriggerPromocode
          ? (event) => {
              event.preventDefault()
              // Pass the path without its query string — applyPromoCode
              // misreads query params.
              const path = stripQueryString(url)
              onTriggerPromocode(path)
            }
          : undefined,
    }
  }

  // B20-1020 - Part 2
  // Legacy model: intent-driven. Removed in the CTA cleanup task.
  const intent = cta.intent ?? 'link'

  if (intent === 'link') {
    if (!cta.href) {
      return undefined
    }

    return {
      text: cta.text,
      variant,
      href: cta.href,
    }
  }

  const resolvedIntent = intentHandlers[intent]

  if (!resolvedIntent?.href) {
    return undefined
  }

  return {
    text: cta.text,
    variant,
    href: resolvedIntent.href,
    onClick: resolvedIntent.onClick,
  }
}
